import React, { useState } from "react";
import axios from "axios";
import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/archive`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
export const archiveApi = {
  get: (path, params) => axios.get(`${BASE}${path}`, { ...auth(), params }).then((r) => r.data),
  post: (path, body) => axios.post(`${BASE}${path}`, body, auth()).then((r) => r.data),
};
const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-sm font-mono text-[10px] tracking-[0.18em] uppercase transition-colors disabled:opacity-40";

/** Group actions for selected items: Archive (residents need a reason) / Unarchive / Delete permanently (super admin, archived only). */
export function BulkArchiveBar({ kind, label = "items", selected, selectedArchived, reasons = [], canDelete, onDone, testid = "bulk" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const n = selected.length;
  const active = selected.filter((id) => !selectedArchived.includes(id));
  const archived = selected.filter((id) => selectedArchived.includes(id));
  const run = async (path, ids, body, msg) => {
    setBusy(true);
    try { const out = await archiveApi.post(`/items/${kind}/${path}`, { ids, ...body }); toast.success(msg(out)); setReason(""); onDone(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); setConfirm(false); }
  };
  if (!n) return null;
  return (
    <div data-testid={`${testid}-bar`} className="flex flex-wrap items-center gap-2 px-3 py-2 border border-nex-gold/30 bg-nex-gold/[0.05] rounded-sm">
      <span data-testid={`${testid}-count`} className="font-mono text-[10px] tracking-[0.18em] uppercase text-nex-gold">{n} selected</span>
      {active.length > 0 && <>
        {kind === "residents" && (
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`}
            className="bg-black/40 border border-white/10 text-nex-silver text-xs px-2 py-1.5 rounded-sm">
            <option value="">Archive reason…</option>
            {reasons.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        )}
        <button type="button" disabled={busy || (kind === "residents" && !reason)} data-testid={`${testid}-archive`}
          onClick={() => run("archive", active, { reason }, (o) => `${o.archived} archived${(o.skipped?.length ?? o.skipped) ? ` · ${o.skipped?.length ?? o.skipped} skipped` : ""}`)}
          className={`${btn} border-amber-300/60 text-amber-200 hover:bg-amber-500/10`}><Archive className="h-3 w-3" /> Archive {active.length}</button>
      </>}
      {archived.length > 0 && <>
        <button type="button" disabled={busy} data-testid={`${testid}-unarchive`} onClick={() => run("unarchive", archived, {}, (o) => `${o.unarchived} unarchived`)}
          className={`${btn} border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/10`}><ArchiveRestore className="h-3 w-3" /> Unarchive {archived.length}</button>
        {canDelete && (
          <button type="button" disabled={busy} data-testid={`${testid}-delete`} onClick={() => setConfirm(true)}
            className={`${btn} border-red-400/50 text-red-300 hover:bg-red-500/10`}><Trash2 className="h-3 w-3" /> Delete permanently {archived.length}</button>
        )}
      </>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`${testid}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {archived.length} archived {label} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              {kind === "residents" ? "Each resident and everything linked to them (notes, check-ins, documents, homework, paychecks, ledger entries and more) is erased. " : ""}
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-delete-no`}>No</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-delete-yes`} onClick={() => run("delete", archived, {}, (o) => `${o.deleted} deleted permanently`)}
              className="bg-red-600 hover:bg-red-700">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
